import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { X, ChevronDown, ChevronUp, Grid3X3 } from 'lucide-react';

const IMAGES = {
  livingDining: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/kshcvxry_shoootin-photo-3.jpg",
  poolGarden: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/i67x1tz7_shoootin-photo-26.jpg",
  poolTerrace: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/bggbgzih_shoootin-photo-24.jpg",
  livingFireplace: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/qlzen3m5_shoootin-photo-2.jpg",
  livingWide: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/umdj218f_shoootin-photo-5.jpg",
  kitchenView: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/xykag7y2_shoootin-photo-4.jpg",
  bathroom: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/8b5th3uo_photo%20salle%20de%20bain%20parents.jpg",
  bedroomEnSuite: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/99ef81xo_shoootin-photo-9.jpg",
  dressing: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/akhbrbpc_shoootin-photo-8.jpg",
  bedroomGarden1: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/8jfafpb8_shoootin-photo-7.jpg",
  bedroomGarden2: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/fspm8k1n_shoootin-photo-6.jpg",
  exteriorGarden: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/jir87wb5_shoootin-photo-27.jpg",
  entrance: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/a41gtbs4_shoootin-photo-25.jpg",
  patio: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/q1oalrx9_shoootin-photo-23.jpg",
  roomPatio: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/gcjees89_shoootin-photo-22.jpg",
  bathroomMarble: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/4egrs46p_shoootin-photo-21.jpg",
  bedroomCloset: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/wz7iiuk3_shoootin-photo-20.jpg",
  laundry: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/l4c8wr58_shoootin-photo-19.jpg",
  cinemaLounge: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/qicuia0z_shoootin-photo-18.jpg",
  bedroomPatio2: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/genx4n9y_shoootin-photo-17.jpg",
  bathroomBlue: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/e2ca6h43_shoootin-photo-16.jpg",
  staircase: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/5bosvbsh_shoootin-photo-15.jpg",
  loungeStairs: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/zwf0hzuv_shoootin-photo-14.jpg",
  showerMarble: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/8zz8qdwl_shoootin-photo-12.jpg",
  officePatio: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/wn46eoqm_shoootin-photo-11.jpg",
  bathroomBlack: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/uukhbrkc_shoootin-photo-10.jpg",
  kitchenIsland: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/bwd8il4b_shoootin-photo-1.jpg",
  livingPanorama: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/4n61azml_shoootin-photo-2.jpg",
  interiorBanana: "https://customer-assets.emergentagent.com/job_maison-prestige-3/artifacts/7kvp946v_shoootin-photo-13.jpg",
};

const GallerySection = () => {
  const { t, language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllMobile, setShowAllMobile] = useState(false);

  const galleryItems = [
    { src: IMAGES.poolGarden, alt: 'Piscine chauffée 4x8m et jardin paysager - Villa luxe Croissy-sur-Seine' },
    { src: IMAGES.livingPanorama, alt: 'Grand salon panoramique avec hauteur sous plafond 3m - Villa contemporaine Croissy' },
    { src: IMAGES.kitchenIsland, alt: 'Cuisine haut de gamme avec îlot central - Villa prestige Yvelines' },
    { src: IMAGES.livingWide, alt: 'Salon de réception lumineux avec baies vitrées - Villa Croissy-sur-Seine' },
    { src: IMAGES.livingFireplace, alt: 'Salon avec cheminée design - Villa contemporaine Croissy' },
    { src: IMAGES.livingDining, alt: 'Espace salle à manger ouvert sur salon - Villa luxe près Paris' },
    { src: IMAGES.interiorBanana, alt: 'Intérieur contemporain avec végétation - Villa architecte Croissy' },
    { src: IMAGES.kitchenView, alt: 'Cuisine ouverte équipée haut de gamme - Villa prestige Croissy-sur-Seine' },
    { src: IMAGES.bedroomGarden1, alt: 'Chambre avec vue jardin et lumière naturelle - Villa luxe Yvelines' },
    { src: IMAGES.bedroomGarden2, alt: 'Chambre avec baie vitrée sur jardin - Villa contemporaine Croissy' },
    { src: IMAGES.bedroomEnSuite, alt: 'Suite parentale 32m² avec salle de bain privative - Villa Croissy' },
    { src: IMAGES.bedroomCloset, alt: 'Chambre avec placards intégrés - Villa prestige Croissy-sur-Seine' },
    { src: IMAGES.bedroomPatio2, alt: 'Chambre avec vue sur patio intérieur - Villa luxe Croissy' },
    { src: IMAGES.dressing, alt: 'Dressing aménagé sur mesure - Villa contemporaine Croissy-sur-Seine' },
    { src: IMAGES.bathroom, alt: 'Salle de bain principale haut de gamme - Villa luxe Yvelines' },
    { src: IMAGES.bathroomMarble, alt: 'Salle de bain en marbre - Villa prestige Croissy-sur-Seine' },
    { src: IMAGES.bathroomBlue, alt: 'Salle de bain avec baignoire îlot - Villa contemporaine Croissy' },
    { src: IMAGES.bathroomBlack, alt: 'Salle de bain design noir - Villa architecte Croissy-sur-Seine' },
    { src: IMAGES.showerMarble, alt: 'Douche italienne en marbre - Villa luxe Croissy-sur-Seine' },
    { src: IMAGES.patio, alt: 'Patio intérieur lumineux - Villa contemporaine Croissy' },
    { src: IMAGES.staircase, alt: 'Escalier design contemporain - Villa prestige Yvelines' },
    { src: IMAGES.loungeStairs, alt: 'Espace détente avec escalier design - Villa Croissy-sur-Seine' },
    { src: IMAGES.roomPatio, alt: 'Pièce donnant sur patio - Villa luxe Croissy' },
    { src: IMAGES.cinemaLounge, alt: 'Home cinéma privé 30m² - Villa prestige Croissy-sur-Seine' },
    { src: IMAGES.officePatio, alt: 'Bureau avec vue sur patio - Villa contemporaine Croissy' },
    { src: IMAGES.entrance, alt: 'Entrée de la villa contemporaine - Croissy-sur-Seine Yvelines' },
    { src: IMAGES.laundry, alt: 'Buanderie équipée - Villa luxe Croissy-sur-Seine' },
    { src: IMAGES.poolTerrace, alt: 'Terrasse avec piscine chauffée et pool house - Villa Croissy' },
    { src: IMAGES.exteriorGarden, alt: 'Jardin méditerranéen paysager 1063m² - Villa luxe Croissy-sur-Seine' },
  ];

  const MOBILE_INITIAL_COUNT = 3;
  const remainingCount = galleryItems.length - MOBILE_INITIAL_COUNT;

  return (
    <section 
      className="py-16 md:py-32 bg-white"
      data-testid="gallery-section"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-8 md:mb-16"
        >
          <h2 
            className="text-2xl md:text-4xl lg:text-5xl text-[#1A1A1A] font-['Playfair_Display'] mb-3 md:mb-4"
            data-testid="gallery-title"
          >
            {t('gallery_title')}
          </h2>
          <p className="text-[#5C5C59] text-sm md:text-lg max-w-3xl">
            {t('gallery_subtitle')}
          </p>
        </motion.div>

        {/* Mobile Gallery - Only visible on small screens */}
        <div className="block md:hidden">
          <div className="space-y-3">
            {galleryItems.map((item, index) => {
              // Hide items beyond first 3 when not expanded
              const isHidden = !showAllMobile && index >= MOBILE_INITIAL_COUNT;
              
              if (isHidden) return null;
              
              return (
                <div
                  key={`mobile-${index}`}
                  className="relative aspect-[4/3] overflow-hidden cursor-pointer group"
                  onClick={() => setSelectedImage(item)}
                  data-testid={`gallery-item-mobile-${index}`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                    loading={index > 2 ? "lazy" : "eager"}
                  />
                  <div className="absolute inset-0 bg-black/0 group-active:bg-black/20 transition-all duration-200" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/50 to-transparent">
                    <p className="text-white text-sm font-medium">{item.alt}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* View More Button - Mobile Only */}
          {!showAllMobile && (
            <button
              onClick={() => setShowAllMobile(true)}
              className="w-full mt-6 py-4 px-6 bg-[#1A2F23] text-white flex items-center justify-center gap-3 active:bg-[#122219] transition-colors"
              data-testid="view-more-btn"
            >
              <Grid3X3 className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">
                {language === 'fr' ? `Voir les ${remainingCount} autres photos` : `View ${remainingCount} more photos`}
              </span>
              <ChevronDown className="w-5 h-5" strokeWidth={1.5} />
            </button>
          )}

          {/* View Less Button - Mobile Only */}
          {showAllMobile && (
            <button
              onClick={() => setShowAllMobile(false)}
              className="w-full mt-6 py-3 px-6 border border-[#1A2F23] text-[#1A2F23] flex items-center justify-center gap-2 active:bg-[#F7F7F5] transition-colors"
              data-testid="view-less-btn"
            >
              <span className="text-sm font-medium">
                {language === 'fr' ? 'Réduire' : 'Show less'}
              </span>
              <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Desktop Gallery - Bento Grid - Only visible on medium+ screens */}
        <div className="hidden md:block">
          <div className="bento-grid">
            {galleryItems.map((item, index) => (
              <div
                key={`desktop-${index}`}
                className={`bento-item-${index + 1} relative overflow-hidden cursor-pointer group`}
                onClick={() => setSelectedImage(item)}
                data-testid={`gallery-item-${index}`}
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover img-hover"
                  loading={index > 5 ? "lazy" : "eager"}
                />
                <div className="absolute inset-0 bg-[#1A2F23]/0 group-hover:bg-[#1A2F23]/20 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white transition-colors z-10"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close-btn"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/80 text-sm">
            {selectedImage.alt}
          </p>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
